import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Users, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useGymMembers } from "@/hooks/schedules/useGymMembers";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";

interface VisibilityConfigDialogProps {
  contentType: "routine" | "workout_plan";
  contentId: string;
  gymId: string;
}

export const VisibilityConfigDialog = ({ contentType, contentId, gymId }: VisibilityConfigDialogProps) => {
  const [open, setOpen] = useState(false);
  const [visibilityType, setVisibilityType] = useState<"all" | "specific">("all");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { data: members, isLoading: loadingMembers } = useGymMembers(gymId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      loadVisibility();
    }
  }, [open]);

  const loadVisibility = async () => {
    setLoading(true);
    try {
      const { data: cv } = await supabase
        .from("content_visibility")
        .select("id, visibility_type")
        .eq("content_type", contentType)
        .eq("content_id", contentId)
        .maybeSingle();

      if (cv) {
        setVisibilityType(cv.visibility_type as "all" | "specific");
        if (cv.visibility_type === "specific") {
          const { data: memberRows } = await supabase
            .from("content_visibility_members")
            .select("user_id")
            .eq("content_visibility_id", cv.id);
          setSelectedMembers(memberRows?.map(m => m.user_id) || []);
        } else {
          setSelectedMembers([]);
        }
      } else {
        setVisibilityType("all");
        setSelectedMembers([]);
      }
    } catch (error) {
      console.error("Error loading visibility:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No autenticado");

      // Upsert content_visibility
      const { data: cv, error: cvError } = await supabase
        .from("content_visibility")
        .upsert({
          content_type: contentType,
          content_id: contentId,
          visibility_type: visibilityType,
          created_by: user.id,
          gym_id: gymId,
          updated_at: new Date().toISOString(),
        }, { onConflict: "content_type,content_id" })
        .select("id")
        .single();

      if (cvError) throw cvError;

      // Delete existing members
      await supabase
        .from("content_visibility_members")
        .delete()
        .eq("content_visibility_id", cv.id);

      // Insert new members if specific
      if (visibilityType === "specific" && selectedMembers.length > 0) {
        const { error: insertError } = await supabase
          .from("content_visibility_members")
          .insert(selectedMembers.map(userId => ({
            content_visibility_id: cv.id,
            user_id: userId,
          })));
        if (insertError) throw insertError;
      }

      queryClient.invalidateQueries({ queryKey: ["content-visibility"] });

      toast({
        title: "Visibilidad actualizada",
        description: visibilityType === "all"
          ? "Visible para todos los socios del gimnasio"
          : `Visible para ${selectedMembers.length} socio(s) seleccionado(s)`,
      });
      setOpen(false);
    } catch (error: any) {
      console.error("Error saving visibility:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo guardar la configuración de visibilidad",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (userId: string) => {
    setSelectedMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="bg-secondary/50 border-border hover:bg-secondary text-muted-foreground hover:text-foreground"
          title="Configurar visibilidad"
        >
          <Users className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-background/95 backdrop-blur-md text-foreground border-border max-w-md w-[90vw]">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-primary">
            Configurar Visibilidad
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <RadioGroup value={visibilityType} onValueChange={(v) => setVisibilityType(v as "all" | "specific")}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="vis-all" />
                <Label htmlFor="vis-all" className="cursor-pointer">Todos los socios del gimnasio</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="vis-specific" />
                <Label htmlFor="vis-specific" className="cursor-pointer">Socios específicos</Label>
              </div>
            </RadioGroup>

            {visibilityType === "specific" && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Seleccioná los socios que podrán ver este contenido:
                </p>
                {loadingMembers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : members && members.length > 0 ? (
                  <ScrollArea className="h-[250px] rounded-md border border-border p-2">
                    <div className="space-y-2">
                      {members.map(member => (
                        <div
                          key={member.id}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                          onClick={() => toggleMember(member.id)}
                        >
                          <Checkbox
                            checked={selectedMembers.includes(member.id)}
                            onCheckedChange={() => toggleMember(member.id)}
                          />
                          <span className="text-sm">
                            {member.first_name || ""} {member.last_name || ""}
                            {!member.first_name && !member.last_name && "Sin nombre"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay socios en este gimnasio
                  </p>
                )}
                {selectedMembers.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {selectedMembers.length} socio(s) seleccionado(s)
                  </p>
                )}
              </div>
            )}

            <Button
              onClick={handleSave}
              disabled={saving || (visibilityType === "specific" && selectedMembers.length === 0)}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
